/**
 * SOTA REST service — wraps the /api/sub-projects/{id}/sota/* endpoints.
 */
import api from './api';

export interface SotaCostEstimate {
  input_tokens: number;
  output_tokens: number;
  estimated_cost_cents: number;
  currency: string;
  model_label: string;
}

export interface ScopingNoteResponse {
  content: string;
  generation_status: string;
}

export interface CoherencePassResponse {
  revised_text: string;
  flag_count: number;
  flags: string[];
}

export interface DraftResponse {
  body_html: string;
  version: number;
  draft_source: string | null;
}

export interface PublishDraftResponse {
  content: string;
  generation_status: string;
}

export const sotaService = {
  /**
   * Estimate the LLM cost for generating the base SOTA draft (step 1).
   */
  async estimateCost(subProjectId: string): Promise<SotaCostEstimate> {
    const response = await api.post<SotaCostEstimate>(
      `/api/sub-projects/${subProjectId}/sota/cost-estimate`,
    );
    return response.data;
  },

  /**
   * Generate and immediately save a preliminary scoping note.
   * Sets generation_status = "scoping" on the sub-project.
   */
  async generateScopingNote(subProjectId: string): Promise<ScopingNoteResponse> {
    const response = await api.post<ScopingNoteResponse>(
      `/api/sub-projects/${subProjectId}/sota/scoping-note`,
    );
    return response.data;
  },

  /**
   * Run the coherence/flag pass on the current SOTA content.
   * Writes result to the CFA draft layer (not directly to the field).
   */
  async runCoherencePass(subProjectId: string): Promise<CoherencePassResponse> {
    const response = await api.post<CoherencePassResponse>(
      `/api/sub-projects/${subProjectId}/sota/coherence-pass`,
    );
    return response.data;
  },

  /**
   * Fetch the current CFA draft for state_of_the_art.
   * Returns null if no draft exists yet.
   */
  async getDraft(subProjectId: string): Promise<DraftResponse | null> {
    const response = await api.get<DraftResponse | null>(
      `/api/sub-projects/${subProjectId}/sota/draft`,
    );
    return response.data ?? null;
  },

  /**
   * Publish the current CFA draft to the state_of_the_art field.
   * Converts HTML → plain text and sets generation_status = 'draft'.
   */
  async publishDraft(subProjectId: string): Promise<PublishDraftResponse> {
    const response = await api.post<PublishDraftResponse>(
      `/api/sub-projects/${subProjectId}/sota/draft/publish`,
    );
    return response.data;
  },

  /**
   * Download the State of the Art field as a .docx file.
   * Triggers a browser download.
   */
  async exportDocx(subProjectId: string, fileName = 'state_of_the_art.docx'): Promise<void> {
    const response = await api.get(`/api/sub-projects/${subProjectId}/sota/export`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
